import type { Appointment } from "@src/packages/care/hooks/useAppointments";
import type {
  Cycle,
  CycleProcedureEvent,
  Phase,
} from "@src/packages/cycles/hooks/useCycles";
import { isPerformedProcedurePhase } from "./performedProcedure";

export const POST_PROCEDURE_TRANSITION_MARKER = "__post_procedure_transition__";
export const POST_PROCEDURE_TRANSITION_VERSION = 1 as const;

export type PostProcedureTransitionProcedure =
  "embryo_transfer" | "insemination" | "ovulation_window";

export type PostProcedureTransitionStatus =
  "dismissed" | "occurred" | "rescheduled" | "pending" | "cancelled";

export type PostProcedureEventKind = CycleProcedureEvent["kind"];
export type PostProcedureEventStatus = CycleProcedureEvent["status"];

export type PostProcedureTransitionCandidate = {
  key: string;
  cycleId: string;
  procedure: PostProcedureTransitionProcedure;
  procedurePhaseType: "transfer" | "insemination" | "luteal_phase";
  /** The scheduled or recorded start of the procedure. */
  procedureDate: string;
  procedureTime: string | null;
  /** The end of the procedure window used for the automatic hand-off prompt. */
  procedureEndDate: string;
  procedureEndTime: string | null;
  source: "phase" | "appointment" | "fixture";
  appointmentId?: string;
  /** Typed event kind used for IUI/OI/TI persistence. */
  procedureEventKind?: PostProcedureEventKind;
  /** Existing event that this candidate is resolving or retrying. */
  procedureEventId?: string;
  procedureEventStatus?: PostProcedureEventStatus;
  procedureEventRecordedAt?: string;
};

export type PostProcedureTransitionRecord = {
  [POST_PROCEDURE_TRANSITION_MARKER]: true;
  version: typeof POST_PROCEDURE_TRANSITION_VERSION;
  key: string;
  cycleId: string;
  procedure: PostProcedureTransitionProcedure;
  status: PostProcedureTransitionStatus;
  procedureDate: string;
  notes: string;
  recordedAt: string;
};

type NoteLike = { text: string; createdAt?: string | null };
type TransitionAppointment = Pick<
  Appointment,
  | "id"
  | "title"
  | "consultationType"
  | "startDate"
  | "startTime"
  | "endDate"
  | "endTime"
  | "cycleId"
> & {
  startTimeLocal?: string;
  endTimeLocal?: string;
};
type TransitionCycle = Pick<Cycle, "id" | "status" | "cycleType">;
type LinkedProcedure = Pick<
  NonNullable<Phase["procedures"]>[number],
  "appointmentId" | "procedureType"
>;
type TransitionPhase = Pick<
  Phase,
  "type" | "startDate" | "endDate" | "startTime"
> & { procedures?: LinkedProcedure[] | null };

/**
 * Procedures each cycle type can contain. Confirming the hand-off records the
 * procedure against the open cycle, so it must never offer one that cycle
 * cannot have, such as an embryo transfer on an egg-freezing cycle.
 */
const PROCEDURES_BY_CYCLE_TYPE: Record<
  Cycle["cycleType"],
  readonly PostProcedureTransitionProcedure[]
> = {
  ivf: ["embryo_transfer"],
  icsi: ["embryo_transfer"],
  fet: ["embryo_transfer"],
  iui: ["insemination"],
  oi: ["ovulation_window", "insemination"],
  egg_freezing: [],
  embryo_freezing: [],
  sperm_freezing: [],
};

function cycleCanHaveProcedure(
  cycle: TransitionCycle,
  procedure: PostProcedureTransitionProcedure,
): boolean {
  return (PROCEDURES_BY_CYCLE_TYPE[cycle.cycleType] ?? []).includes(procedure);
}

/**
 * Words that mark an appointment as being about a procedure without being the
 * procedure itself: a mock transfer, a consult, prep, or the beta afterwards.
 */
const NOT_THE_PROCEDURE =
  /\b(mock|trial|dummy|consult|consultation|planning|pre|prep|preparation|post|beta)\b/;
/** Monitoring visits mention ovulation without being the ovulation window. */
const OVULATION_MONITORING =
  /\b(scan|ultrasound|monitoring|check|test|tracking|bloodwork)\b/;

/**
 * The procedure an appointment was booked for, when the treatment hub linked
 * the two: the phase procedure stores the appointment's id. `undefined` means
 * no link, and `null` a link that names no procedure type.
 */
function linkedProcedureType(
  phases: readonly TransitionPhase[],
  appointmentId: string,
): LinkedProcedure["procedureType"] | undefined {
  for (const phase of phases) {
    for (const procedure of phase.procedures ?? []) {
      if (procedure.appointmentId === appointmentId) {
        return procedure.procedureType;
      }
    }
  }
  return undefined;
}

/** Retrieval and collection are real procedures but not hand-offs to a wait. */
function procedureForLinkedType(
  procedureType: NonNullable<LinkedProcedure["procedureType"]>,
): {
  procedure: PostProcedureTransitionProcedure;
  procedureEventKind?: PostProcedureEventKind;
} | null {
  switch (procedureType) {
    case "embryo_transfer":
      return { procedure: "embryo_transfer" };
    case "insemination":
      return { procedure: "insemination", procedureEventKind: "insemination" };
    default:
      return null;
  }
}

/**
 * Stopgap for appointments with no typed link: provider-created, imported or
 * calendar-added ones (louise-life/dashboard-api#1880). The procedure is
 * inferred from whole words in the title and consultation type. The matching
 * is deliberately conservative: a missed prompt leaves the manual path intact,
 * while a false one records a procedure that never happened.
 */
function procedureForAppointment(appointment: TransitionAppointment): {
  procedure: PostProcedureTransitionProcedure;
  procedureEventKind?: PostProcedureEventKind;
} | null {
  const activityText = [
    appointment.title,
    ...(appointment.consultationType ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (NOT_THE_PROCEDURE.test(activityText)) return null;
  if (/\b(embryo transfer|fet)\b/.test(activityText)) {
    return { procedure: "embryo_transfer" };
  }
  if (/\b(intrauterine|insemination|iui)\b/.test(activityText)) {
    return { procedure: "insemination", procedureEventKind: "insemination" };
  }
  if (/\btimed intercourse\b/.test(activityText)) {
    return {
      procedure: "ovulation_window",
      procedureEventKind: "timed_intercourse",
    };
  }
  if (
    /\btrigger (shot|injection)\b/.test(activityText) ||
    (/\bovulation\b/.test(activityText) &&
      !OVULATION_MONITORING.test(activityText))
  ) {
    return {
      procedure: "ovulation_window",
      procedureEventKind: "ovulation_window",
    };
  }
  return null;
}

function procedureForPhase(
  phase: TransitionPhase,
): Pick<
  PostProcedureTransitionCandidate,
  "procedure" | "procedurePhaseType"
> | null {
  if (phase.type === "transfer") {
    return {
      procedure: "embryo_transfer",
      procedurePhaseType: "transfer",
    };
  }
  if (phase.type === "insemination") {
    return {
      procedure: "insemination",
      procedurePhaseType: "insemination",
    };
  }
  return null;
}

function phaseTime(phase: TransitionPhase): string | null {
  if (!phase.startTime) return null;
  return phase.startTime.slice(0, 5);
}

function appointmentTime(appointment: TransitionAppointment): string | null {
  const raw = appointment.startTimeLocal ?? appointment.startTime;
  if (!raw) return null;
  const match = /(?:T|^)(\d{2}:\d{2})/.exec(raw);
  return match?.[1] ?? null;
}

function appointmentEndTime(appointment: TransitionAppointment): string | null {
  const raw = appointment.endTimeLocal ?? appointment.endTime;
  if (!raw) return null;
  const match = /(?:T|^)(\d{2}:\d{2})/.exec(raw);
  return match?.[1] ?? null;
}

function dateOnly(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = value.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
}

function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

const DEFAULT_PROCEDURE_DURATION_MINUTES: Record<
  PostProcedureTransitionProcedure,
  number
> = {
  embryo_transfer: 45,
  insemination: 30,
  ovulation_window: 60,
};

function localDateFor(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
}

function localTimeFor(date: Date): string {
  return `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes(),
  ).padStart(2, "0")}`;
}

function projectedProcedureEnd(input: {
  procedure: PostProcedureTransitionProcedure;
  procedureDate: string;
  procedureTime: string | null;
}): Pick<
  PostProcedureTransitionCandidate,
  "procedureEndDate" | "procedureEndTime"
> {
  if (!input.procedureTime) {
    return {
      procedureEndDate: input.procedureDate,
      procedureEndTime: null,
    };
  }

  const start = new Date(`${input.procedureDate}T${input.procedureTime}:00`);
  if (Number.isNaN(start.getTime())) {
    return {
      procedureEndDate: input.procedureDate,
      procedureEndTime: null,
    };
  }

  start.setMinutes(
    start.getMinutes() + DEFAULT_PROCEDURE_DURATION_MINUTES[input.procedure],
  );
  return {
    procedureEndDate: localDateFor(start),
    procedureEndTime: localTimeFor(start),
  };
}

/**
 * Keeps the original procedure duration when a scheduled start moves to a
 * new date/time. Date-only reschedules remain date-only.
 */
export function rescheduledProcedureEnd(input: {
  candidate: PostProcedureTransitionCandidate;
  procedureDate: string;
  procedureTime: string | null;
}): Pick<
  PostProcedureTransitionCandidate,
  "procedureEndDate" | "procedureEndTime"
> {
  if (!input.procedureTime || !input.candidate.procedureTime) {
    return {
      procedureEndDate: input.procedureDate,
      procedureEndTime: null,
    };
  }

  const originalStart = new Date(
    `${input.candidate.procedureDate}T${input.candidate.procedureTime}:00`,
  );
  const originalEnd = input.candidate.procedureEndTime
    ? new Date(
        `${input.candidate.procedureEndDate}T${input.candidate.procedureEndTime}:00`,
      )
    : null;
  const nextStart = new Date(
    `${input.procedureDate}T${input.procedureTime}:00`,
  );
  if (
    Number.isNaN(originalStart.getTime()) ||
    !originalEnd ||
    Number.isNaN(originalEnd.getTime()) ||
    Number.isNaN(nextStart.getTime())
  ) {
    return {
      procedureEndDate: input.procedureDate,
      procedureEndTime: null,
    };
  }

  const durationMs = originalEnd.getTime() - originalStart.getTime();
  if (durationMs <= 0) {
    return {
      procedureEndDate: input.procedureDate,
      procedureEndTime: input.procedureTime,
    };
  }

  const nextEnd = new Date(nextStart.getTime() + durationMs);
  return {
    procedureEndDate: localDateFor(nextEnd),
    procedureEndTime: localTimeFor(nextEnd),
  };
}

export function makePostProcedureTransitionKey(
  cycleId: string,
  procedure: PostProcedureTransitionProcedure,
  procedureDate: string,
): string {
  return `${cycleId}:${procedure}:${procedureDate}`;
}

/**
 * Finds the next transition that needs patient confirmation. A recorded
 * luteal phase means the patient has already crossed into the waiting surface,
 * so the hand-off prompt must disappear.
 */
export function resolvePostProcedureTransitionCandidate(input: {
  cycle: TransitionCycle | null | undefined;
  phases: TransitionPhase[];
  appointments: TransitionAppointment[];
  today: string;
  procedureEvents?: readonly PostProcedureTransitionEvent[];
  fixtureProcedure?: "transfer" | "insemination" | "oi" | null;
}): PostProcedureTransitionCandidate | null {
  const {
    cycle,
    phases,
    appointments,
    today,
    procedureEvents = [],
    fixtureProcedure,
  } = input;
  if (!cycle || cycle.status !== "open") return null;
  if (
    phases.some((phase) => phase.type === "luteal_phase" && phase.startDate)
  ) {
    return null;
  }

  if (fixtureProcedure) {
    const procedure =
      fixtureProcedure === "transfer"
        ? "embryo_transfer"
        : fixtureProcedure === "insemination"
          ? "insemination"
          : "ovulation_window";
    // Fixtures obey the same rule, so a route that pairs a procedure with the
    // wrong cycle type renders nothing instead of an impossible state.
    if (!cycleCanHaveProcedure(cycle, procedure)) return null;
    const candidate: PostProcedureTransitionCandidate = {
      key: makePostProcedureTransitionKey(cycle.id, procedure, today),
      cycleId: cycle.id,
      procedure,
      procedurePhaseType:
        fixtureProcedure === "transfer"
          ? "transfer"
          : fixtureProcedure === "insemination"
            ? "insemination"
            : "luteal_phase",
      procedureDate: today,
      procedureTime:
        fixtureProcedure === "transfer"
          ? "14:00"
          : fixtureProcedure === "insemination"
            ? "10:30"
            : "12:00",
      ...(fixtureProcedure === "insemination"
        ? { procedureEventKind: "insemination" as const }
        : fixtureProcedure === "oi"
          ? { procedureEventKind: "ovulation_window" as const }
          : {}),
      ...projectedProcedureEnd({
        procedure,
        procedureDate: today,
        procedureTime:
          fixtureProcedure === "transfer"
            ? "14:00"
            : fixtureProcedure === "insemination"
              ? "10:30"
              : "12:00",
      }),
      source: "fixture",
    };
    return addProcedureEventState(candidate, procedureEvents);
  }

  const phaseCandidate = phases
    .filter(
      (phase) =>
        phase.startDate &&
        phase.startDate <= today &&
        isPerformedProcedurePhase(phase),
    )
    .map((phase) => {
      const procedure = procedureForPhase(phase);
      const procedureDate = dateOnly(phase.startDate);
      if (
        !procedure ||
        !procedureDate ||
        !cycleCanHaveProcedure(cycle, procedure.procedure)
      ) {
        return null;
      }
      return {
        key: makePostProcedureTransitionKey(
          cycle.id,
          procedure.procedure,
          procedureDate,
        ),
        cycleId: cycle.id,
        ...procedure,
        procedureDate,
        procedureTime: phaseTime(phase),
        ...projectedProcedureEnd({
          procedure: procedure.procedure,
          procedureDate,
          procedureTime: phaseTime(phase),
        }),
        source: "phase" as const,
      } satisfies PostProcedureTransitionCandidate;
    })
    .filter(
      (candidate): candidate is Exclude<typeof candidate, null> =>
        candidate !== null,
    )
    .sort((a, b) => b.procedureDate.localeCompare(a.procedureDate))[0];

  if (phaseCandidate) {
    return addProcedureEventState(phaseCandidate, procedureEvents);
  }

  const appointment = appointments
    .filter((item) => {
      // Appointments come from `useAppointments`, which is date-scoped but
      // NOT cycle-scoped. Without this guard, a prior cycle's transfer that
      // happens to sit in the window would auto-open the "Ton transfert
      // embryonnaire a-t-il eu lieu ?" dialog on a brand-new cycle (#3382).
      if (item.cycleId && item.cycleId !== cycle.id) return false;
      return dateOnly(item.startDate) === today;
    })
    .map((item) => {
      const linkedType = linkedProcedureType(phases, item.id);
      const procedure = linkedType
        ? procedureForLinkedType(linkedType)
        : procedureForAppointment(item);
      const procedureDate = dateOnly(item.startDate);
      if (
        !procedure ||
        !procedureDate ||
        !cycleCanHaveProcedure(cycle, procedure.procedure)
      ) {
        return null;
      }
      const procedureTime = appointmentTime(item);
      const explicitEndTime = appointmentEndTime(item);
      const projectedEnd = projectedProcedureEnd({
        procedure: procedure.procedure,
        procedureDate,
        procedureTime,
      });
      return {
        key: makePostProcedureTransitionKey(
          cycle.id,
          procedure.procedure,
          procedureDate,
        ),
        cycleId: cycle.id,
        procedure: procedure.procedure,
        procedurePhaseType:
          procedure.procedure === "embryo_transfer"
            ? "transfer"
            : procedure.procedure === "insemination"
              ? "insemination"
              : "luteal_phase",
        procedureDate,
        procedureTime,
        procedureEndDate: explicitEndTime
          ? (dateOnly(item.endDate) ?? procedureDate)
          : projectedEnd.procedureEndDate,
        procedureEndTime: explicitEndTime ?? projectedEnd.procedureEndTime,
        source: "appointment" as const,
        appointmentId: item.id,
        ...(procedure.procedureEventKind
          ? { procedureEventKind: procedure.procedureEventKind }
          : {}),
      } satisfies PostProcedureTransitionCandidate;
    })
    .filter(
      (candidate): candidate is Exclude<typeof candidate, null> =>
        candidate !== null,
    )[0];

  return appointment
    ? addProcedureEventState(appointment, procedureEvents)
    : null;
}

export type PostProcedureTransitionEvent = Pick<
  CycleProcedureEvent,
  | "id"
  | "cycleId"
  | "kind"
  | "status"
  | "scheduledStartAt"
  | "occurredAt"
  | "recordedAt"
>;

function addProcedureEventState(
  candidate: PostProcedureTransitionCandidate,
  events: readonly PostProcedureTransitionEvent[],
): PostProcedureTransitionCandidate {
  const eventKind =
    candidate.procedureEventKind ??
    (candidate.procedure === "insemination" ? "insemination" : null);
  if (!eventKind) return candidate;

  const event = events
    .filter(
      (item) => item.cycleId === candidate.cycleId && item.kind === eventKind,
    )
    .filter((item) => {
      const eventDate = dateOnly(item.occurredAt ?? item.scheduledStartAt);
      return eventDate === candidate.procedureDate;
    })
    .sort((a, b) => b.recordedAt.localeCompare(a.recordedAt))[0];

  if (!event) return candidate;
  return {
    ...candidate,
    procedureEventKind: eventKind,
    procedureEventId: event.id,
    procedureEventStatus: event.status,
    procedureEventRecordedAt: event.recordedAt,
  };
}

export function parsePostProcedureTransitionRecord(
  text: string | null | undefined,
): PostProcedureTransitionRecord | null {
  if (!text) return null;
  try {
    const value: unknown = JSON.parse(text);
    if (!value || typeof value !== "object") return null;
    const record = value as Record<string, unknown>;
    if (
      record[POST_PROCEDURE_TRANSITION_MARKER] !== true ||
      record.version !== POST_PROCEDURE_TRANSITION_VERSION ||
      typeof record.key !== "string" ||
      typeof record.cycleId !== "string" ||
      typeof record.procedure !== "string" ||
      typeof record.status !== "string" ||
      typeof record.procedureDate !== "string" ||
      typeof record.recordedAt !== "string"
    ) {
      return null;
    }
    if (
      !["embryo_transfer", "insemination", "ovulation_window"].includes(
        record.procedure,
      ) ||
      ![
        "dismissed",
        "occurred",
        "rescheduled",
        "pending",
        "cancelled",
      ].includes(record.status)
    ) {
      return null;
    }
    return {
      [POST_PROCEDURE_TRANSITION_MARKER]: true,
      version: POST_PROCEDURE_TRANSITION_VERSION,
      key: record.key,
      cycleId: record.cycleId,
      procedure: record.procedure as PostProcedureTransitionProcedure,
      status: record.status as PostProcedureTransitionStatus,
      procedureDate: record.procedureDate,
      notes: typeof record.notes === "string" ? record.notes : "",
      recordedAt: record.recordedAt,
    };
  } catch {
    return null;
  }
}

export function getLatestPostProcedureTransitionRecord(
  notes: readonly NoteLike[] | undefined,
  key: string,
): PostProcedureTransitionRecord | null {
  if (!notes) return null;
  return notes.reduce<PostProcedureTransitionRecord | null>((latest, note) => {
    const record = parsePostProcedureTransitionRecord(note.text);
    if (!record || record.key !== key) return latest;
    if (!latest || record.recordedAt > latest.recordedAt) return record;
    return latest;
  }, null);
}

function minutesForTime(value: string | null): number | null {
  if (!value) return null;
  const match = /^(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * The automatic transition is actionable after the procedure's projected end
 * in the patient's local timezone. Date-only procedures stay actionable
 * throughout the calendar day. The hand-off card can still be opened manually
 * before then when the patient wants to move ahead sooner.
 */
export function isPostProcedureTransitionTimeReached(input: {
  candidate: PostProcedureTransitionCandidate;
  today: string;
  now?: Date;
}): boolean {
  const { candidate, today, now = new Date() } = input;
  if (candidate.procedureEndDate < today) return true;
  if (candidate.procedureEndDate > today) return false;

  const procedureEndMinutes = minutesForTime(candidate.procedureEndTime);
  if (procedureEndMinutes == null) return true;
  return now.getHours() * 60 + now.getMinutes() >= procedureEndMinutes;
}

export function shouldAutoPromptPostProcedureTransition(input: {
  candidate: PostProcedureTransitionCandidate;
  notes: readonly NoteLike[] | undefined;
  today: string;
  now?: Date;
}): boolean {
  const eventStatus = input.candidate.procedureEventStatus;
  if (
    eventStatus === "pending_confirmation" ||
    eventStatus === "rescheduled" ||
    eventStatus === "cancelled"
  ) {
    return false;
  }
  if (
    eventStatus === "dismissed" &&
    input.candidate.procedureEventRecordedAt?.slice(0, 10) === input.today
  ) {
    return false;
  }

  if (
    !isPostProcedureTransitionTimeReached({
      candidate: input.candidate,
      today: input.today,
      now: input.now,
    })
  ) {
    return false;
  }

  const latest = getLatestPostProcedureTransitionRecord(
    input.notes,
    input.candidate.key,
  );
  if (!latest) return true;
  if (
    ["occurred", "rescheduled", "pending", "cancelled"].includes(latest.status)
  ) {
    return false;
  }
  // Dismissal is a snooze for the day. The persistent hand-off card remains
  // available so the patient can reopen the prompt whenever they are ready.
  if (latest.recordedAt.slice(0, 10) !== input.today) return true;

  // If the patient dismissed the prompt before the projected procedure end,
  // it should become eligible again once that window has passed.
  const dismissedAt = new Date(latest.recordedAt);
  const procedureEndMinutes = minutesForTime(input.candidate.procedureEndTime);
  if (
    procedureEndMinutes != null &&
    !Number.isNaN(dismissedAt.getTime()) &&
    localDateFor(dismissedAt) === input.today
  ) {
    return (
      dismissedAt.getHours() * 60 + dismissedAt.getMinutes() <
      procedureEndMinutes
    );
  }

  return false;
}

export function serializePostProcedureTransitionRecord(
  record: Omit<
    PostProcedureTransitionRecord,
    typeof POST_PROCEDURE_TRANSITION_MARKER | "version"
  >,
): string {
  return JSON.stringify({
    [POST_PROCEDURE_TRANSITION_MARKER]: true,
    version: POST_PROCEDURE_TRANSITION_VERSION,
    ...record,
  } satisfies PostProcedureTransitionRecord);
}

export function nextDayForPostProcedure(iso: string): string {
  return addDays(iso, 1);
}
